import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "../../lib/api";
import { localeOf } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { StatusBadge } from "../../components/StatusBadge";

const STATUSES = ["new", "contacted", "qualified", "closed"];

export default function PartnerLeads() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const locale = localeOf(i18n.language);

  const load = () => api.get("/partner-leads").then(({ data }) => setLeads(data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/partner-leads/${id}`, { status }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="partner-leads-page">
      <h1 className="font-display text-3xl font-extrabold">{t("nav.leads")}</h1>
      <div className="vy-card overflow-x-auto p-5">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-muted-foreground">
              <th className="py-2">{t("admin.name")}</th>
              <th className="py-2">{t("pub.form.business")}</th>
              <th className="py-2">{t("pub.form.city")}</th>
              <th className="py-2">{t("pub.form.goal")}</th>
              <th className="py-2">{t("common.language")}</th>
              <th className="py-2">{t("profile.history")}</th>
              <th className="py-2">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} className="border-t border-border" data-testid={`lead-row-${l.id}`}>
                <td className="py-3">
                  <p className="font-semibold">{l.first_name} {l.last_name}</p>
                  <p className="text-xs text-muted-foreground">{l.email}{l.phone ? ` · ${l.phone}` : ""}</p>
                </td>
                <td className="py-3">
                  {l.business_name}
                  {l.business_type && <p className="text-xs text-muted-foreground">{l.business_type}</p>}
                </td>
                <td className="py-3 text-muted-foreground">{l.city}</td>
                <td className="max-w-[260px] py-3 text-xs text-muted-foreground">
                  {l.growth_goal || l.message || "—"}
                </td>
                <td className="py-3 text-xs font-bold uppercase">{l.language}</td>
                <td className="py-3 text-xs text-muted-foreground">
                  {fmtDate(l.created_at, locale)}
                  <p>{l.utm_source ? `utm: ${l.utm_source}` : l.source}</p>
                </td>
                <td className="py-3">
                  {["admin", "super_admin"].includes(user?.role) ? (
                    <select data-testid={`lead-status-select-${l.id}`} value={l.status || "new"}
                      onChange={(e) => setStatus(l.id, e.target.value)}
                      className="h-10 rounded-full border border-input px-3 text-xs font-semibold">
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{t(`system.leadStatus.${s}`, s)}</option>
                      ))}
                    </select>
                  ) : <StatusBadge status={l.status || "new"} />}
                </td>
              </tr>
            ))}
            {leads.length === 0 && (
              <tr><td colSpan={7} className="py-4 text-sm text-muted-foreground">{t("common.none")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
